import {
  APIInventorySearchResponse,
  APIInventoryVisibleChildrenResponse,
  APISearchResponse,
  ArdoqId,
  BooleanOperator,
  DataSourceType,
  Operator,
  QueryBuilderSubquery,
  SearchContext,
  SearchContextRule,
  InventoryEntityType,
  InventorySaveSettingsPayload,
  APIInventorySavedSettings,
  APIComponentSuggestion,
  BaseFetchInventoryDataParams,
  FetchInventoryDataForReportParams,
  FetchInventoryDataParams,
  FetchReferenceInventoryDataParams,
  APIColumnsResponse,
  APIReportFilters,
  SortOrder,
  QueryBuilderQuery,
  InventoryExcelExportPayload,
  FetchInventoryDatasetsParams,
  LoadedStateParams,
} from '@ardoq/api-types';
import { ApiResponse } from './types';
import {
  BlobWithFileName,
  fetchBlobWithFileName,
  fetchJson,
  Method,
  toRequestOptions,
} from './api';
import { Maybe } from '@ardoq/common-helpers';

const COMPONENT_SEARCH_CONTEXT_RULE: SearchContextRule = {
  field: 'type',
  id: 'type',
  input: 'select',
  operator: Operator.EQUAL,
  type: 'string',
  value: SearchContext.COMPONENT,
} as const;

const REFERENCE_SEARCH_CONTEXT_RULE: SearchContextRule = {
  field: 'type',
  id: 'type',
  input: 'select',
  operator: Operator.EQUAL,
  type: 'string',
  value: SearchContext.REFERENCE,
} as const;

const toInventoryQuery = (
  searchContextRule: SearchContextRule,
  subquery: QueryBuilderSubquery
): QueryBuilderQuery => ({
  condition: BooleanOperator.AND,
  rules: [searchContextRule, subquery],
});

const getInventoryDataPayload = (
  searchContextRule: SearchContextRule,
  { from, size, sorting, subquery }: BaseFetchInventoryDataParams
) => ({
  datasource: DataSourceType.ADVANCED_SEARCH as const,
  from,
  sort: sorting?.sortBy,
  order: sorting?.order,
  query: toInventoryQuery(searchContextRule, subquery),
  size,
});

const buildInventorySearchRequest = ({
  from,
  size,
  sorting,
  subquery,
  columns,
  hierarchy,
  includeTotalCounts,
  parentId,
}: FetchInventoryDataParams): Request => {
  const payload = {
    ...getInventoryDataPayload(COMPONENT_SEARCH_CONTEXT_RULE, {
      from,
      size,
      sorting,
      subquery,
    }),
    columns,
    hierarchy,
    parentId,
    includeTotalCounts,
  };
  return new Request(
    '/api/inventory/search',
    toRequestOptions(Method.POST, payload)
  );
};

const fetchInventoryData = (
  params: FetchInventoryDataParams
): ApiResponse<APISearchResponse> =>
  fetchJson<APISearchResponse>(buildInventorySearchRequest(params));

const fetchReferenceInventoryData = ({
  columns,
  ...params
}: FetchReferenceInventoryDataParams): ApiResponse<APISearchResponse> => {
  const payload = {
    ...getInventoryDataPayload(REFERENCE_SEARCH_CONTEXT_RULE, params),
    columns,
  };
  const request = new Request(
    '/api/inventory/search',
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson<APISearchResponse>(request);
};

/**
 * Fetches one parent's direct children.
 */
const fetchVisibleChildren = (
  params: FetchInventoryDataParams & {
    hierarchy: 'hierarchyCollapsed';
    parentId: ArdoqId;
  }
): ApiResponse<APIInventoryVisibleChildrenResponse> =>
  fetchJson<APIInventoryVisibleChildrenResponse>(
    buildInventorySearchRequest(params)
  );

type InventoryDrilldownPayload = {
  datasource: DataSourceType.ADVANCED_SEARCH;
  from: number;
  size: number;
  sort?: string; // undefined for column request
  order?: SortOrder; // undefined for column request
  query: QueryBuilderQuery;
  workspaceIds: ArdoqId[] | null;
  filters: Maybe<APIReportFilters>;
  cacheControl: 'no-cache' | undefined; // undefined for column request
};

const fetchInventoryDataForReport = ({
  from,
  size,
  sorting,
  subquery,
  workspaceIds,
  reportId,
  filters,
}: FetchInventoryDataForReportParams): ApiResponse<APIInventorySearchResponse> => {
  const payload: InventoryDrilldownPayload = {
    ...getInventoryDataPayload(COMPONENT_SEARCH_CONTEXT_RULE, {
      from,
      size,
      sorting,
      subquery,
    }),
    workspaceIds,
    filters,
    cacheControl: size === 0 ? undefined : 'no-cache', // we need fresh data when we close component overview page or do change e.g. to parent field
  };
  const url = new URL(
    `/api/inventory/${reportId}/drilldown`,
    window.location.origin
  );
  const request = new Request(url, toRequestOptions(Method.POST, payload));

  return fetchJson<APIInventorySearchResponse>(request);
};

const getComponentSuggestionsForReferenceCreation = ({
  referenceTypeName,
  componentTypeName,
  searchString,
  direction,
}: {
  referenceTypeName?: string;
  componentTypeName: string;
  searchString: string;
  direction: 'incoming' | 'outgoing';
}) => {
  /*
   * For incoming references, we use the referenceTypeName as a constraint to limit the set of possible suggestions.
   * We also use the returned referenceTypeId for each suggestion to aid in reference creation.
   * For outgoing references, we don't use the referenceTypeName and we also don't get the referenceTypeId back.
   * */

  const request = new Request(
    `/api/inventory/suggestion/component?q=${encodeURIComponent(searchString)}&limit=100`,
    toRequestOptions(Method.POST, {
      referenceTypeName,
      componentTypeName,
      direction,
    })
  );
  return fetchJson<Array<APIComponentSuggestion>>(request);
};

const saveUserSettings = (
  payload: InventorySaveSettingsPayload
): ApiResponse<APIInventorySavedSettings> => {
  const request = new Request(
    `/api/inventory/settings`,
    toRequestOptions(Method.PUT, payload)
  );
  return fetchJson<APIInventorySavedSettings>(request);
};

type GetUserSettingsParams = {
  entityType: InventoryEntityType;
} & ({ workspaceIds: ArdoqId[] } | { componentTypeIds: string[] });

const fetchUserSettings = (
  params: GetUserSettingsParams
): ApiResponse<APIInventorySavedSettings> => {
  const request = new Request(
    `/api/inventory/get-settings`,
    toRequestOptions(Method.POST, params)
  );
  return fetchJson<APIInventorySavedSettings>(request);
};

const fetchInventoryDatasets = ({
  datasetDefinitions,
  from,
  size,
  sorting,
  subquery,
  columns,
  hierarchy,
}: FetchInventoryDatasetsParams): ApiResponse<APISearchResponse> => {
  const payload = {
    ...getInventoryDataPayload(COMPONENT_SEARCH_CONTEXT_RULE, {
      from,
      size,
      sorting,
      subquery,
    }),
    columns,
    hierarchy,
    datasetDefinitions,
  };
  const request = new Request(
    '/api/inventory/datasets',
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson<APISearchResponse>(request);
};

const getInventoryColumnsPayload = (input: InventoryColumnsInput) => {
  if (input.type === 'datasets') {
    return { datasetDefinitions: input.payload };
  }

  return {
    query: toInventoryQuery(COMPONENT_SEARCH_CONTEXT_RULE, input.payload),
  };
};

type InventoryColumnsInput =
  | { type: 'datasets'; payload: LoadedStateParams[] }
  | { type: 'query'; payload: QueryBuilderSubquery };

const fetchColumns = (payload: object): ApiResponse<APIColumnsResponse> => {
  const request = new Request(
    `/api/inventory/columns`,
    toRequestOptions(Method.POST, payload)
  );
  return fetchJson<APIColumnsResponse>(request);
};

const fetchInventoryColumns = (
  input: InventoryColumnsInput
): ApiResponse<APIColumnsResponse> =>
  fetchColumns(getInventoryColumnsPayload(input));

const fetchReferenceInventoryColumns = (
  subquery: QueryBuilderSubquery
): ApiResponse<APIColumnsResponse> =>
  fetchColumns({
    query: toInventoryQuery(REFERENCE_SEARCH_CONTEXT_RULE, subquery),
  });

const exportAs = (
  definition: InventoryExcelExportPayload
): ApiResponse<BlobWithFileName> => {
  const request = new Request(
    '/api/inventory/export',
    toRequestOptions(Method.POST, definition)
  );
  return fetchBlobWithFileName(request);
};

export const inventoryApi = {
  fetchInventoryData,
  fetchVisibleChildren,
  getComponentSuggestionsForReferenceCreation,
  saveUserSettings,
  fetchUserSettings,
  fetchInventoryDataForReport,
  fetchInventoryColumns,
  fetchReferenceInventoryData,
  fetchReferenceInventoryColumns,
  exportAs,
  fetchInventoryDatasets,
};
